<template>
  <div class="login-container">
    <el-card class="login-box">      
      <h2 class="login-title">{{ $t('message.login.title') }}</h2>
      <el-form 
        :model="loginForm" 
        :rules="optimizedRules" 
        ref="loginFormRef"
        class="login-form"
      >
        <el-form-item prop="username">
          <el-input
            v-model="loginForm.username"        
            :placeholder="$t('message.login.username')"
            size="large"
            :prefix-icon="User"
            @input="realtimeValidate('username')"
            @blur="validateField('username')"
            class="custom-icon-input"
          />
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            :placeholder="$t('message.login.password')"
            type="password"
            size="large"
            :prefix-icon="Lock"
            show-password              
            @input="realtimeValidate('password')"
            @blur="validateField('password')"
            class="custom-icon-input"
          />
        </el-form-item>
        
        <!-- 记住用户名 -->
        <el-form-item>
          <el-checkbox v-model="loginForm.remember">{{ $t('message.login.rememberChek') }}</el-checkbox>
        </el-form-item>
        
        <el-form-item>
          <el-button
            type="primary"
            size="large"
            @click="handleLogin"
            :loading="loading"
            class="login-btn"
          >
            {{ $t('message.login.loginBtn') }}            
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template> 

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { User, Lock } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'  
import { md5 } from 'js-md5'
import { useI18n } from 'vue-i18n'
import { getConfig } from '@/utils/configTool';
import { RTU_USER_ROLE_ID } from '@/utils/roles'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const store = useStore()

// 使用 ref 引用表单
const loginFormRef = ref()

const elLocale = ref(null);
const loginForm = ref({
  username: '',
  password: '',
  remember: false  
})

// 修复表单验证方法
const realtimeValidate = (field) => {
  if (!loginForm.value[field]) {
    loginFormRef.value?.clearValidate(field)
    return
  }
  validateField(field)
}

const validateField = (field) => {
  loginFormRef.value?.validateField(field)
}

const optimizedRules = {
  username: [     
    { required: true, message: t('message.login.usernameRequired'), trigger: 'blur' }  
  ],
  password: [
    { required: true, message: t('message.login.passwordRequired'), trigger: 'blur' }  
  ]
}

// 组件加载时检查是否有保存的用户名

// ===== 生命周期 =====
onMounted(async () => {
 // 从 sessionStorage 恢复用户名
  const savedUsername = sessionStorage.getItem('savedUsername')  
  if (savedUsername) {
    loginForm.value.username = savedUsername
    loginForm.value.remember = true
  }
 
 // ✅ 检查 langType，如果是 1，强制当前页显示英文（不保存到 localStorage）
  try {
    const baseConfig = await getConfig();
    if (baseConfig.langType == 1) {
      console.log('🔵 langType == 1,登录页强制显示英文');
      
      // ✅ 只设置当前页的语言，不保存到 localStorage
      locale.value = 'en';
      
      // ✅ 加载 Element Plus 英文语言包
      await loadLocale('en');
      
      // ⚠️ 注意：不执行 localStorage.setItem('lang', 'en')
      // 这样其他页面仍然使用用户之前设置的语言
    } else {
      // langType != 1，使用用户之前保存的语言设置
      const savedLang = localStorage.getItem('lang') || 'en';
      locale.value = savedLang;
      await loadLocale(savedLang);
    }
  } catch (error) {
    console.error('获取配置失败:', error);
    // 出错时使用默认语言
    const savedLang = localStorage.getItem('lang') || 'en';
    locale.value = savedLang;
    await loadLocale(savedLang);
  }
});
 

const loading = ref(false)

// ===== 登录处理 =====
const handleLogin = async () => {
  // 1. 表单验证
  try {
    await loginFormRef.value.validate()
  } catch (error) {
    ElMessage.warning(t('message.login.fillAllFields'))
    return
  }
  
  // 2. 处理"记住用户名"选项
  if (loginForm.value.remember) {
    sessionStorage.setItem('savedUsername', loginForm.value.username)
  } else {
    sessionStorage.removeItem('savedUsername')
  }
  
  loading.value = true
  
  try {
    // 3. 调用 Vuex store 的 login action
    const loginData = {
      username: loginForm.value.username.trim(),
      password: md5(loginForm.value.password.trim())
    }
    
    const result = await store.dispatch('user/login', loginData)
    
    if (!result || !result.success) {
      ElMessage.error(result?.message || t('message.login.failed'))
      loading.value = false
      return
    }

    // 4. 获取用户信息
    //const { user, menus, permissions } = result.data
    
    // 5. 设置语言
    const lang = result.data.lang ||  'en'//'zh-CN'
    await changeLang(lang)
    
    // 6. 跳转
    //const redirectPath = route.query.redirect || '/main/dashboard'
    //const  redirectPath = route.query.redirect || '/main/clientProjectDeail?businessId=1&businessName=Digi-RTU'
    //await router.push(redirectPath)    


          // 根据登录类型决定跳转路径
      let redirectPath = '/main/clientProjectDeail?businessId=1&businessName=Digi-RTU';//'/main/dashboard'
      //ElMessage.error(`${redirectPath} handleLogin`);
      let finalPath = route.query.redirect || redirectPath

      // RTU User 角色直达 RTU 状态页（路由白名单见 router.js；服务端 403 授权见
      // Digi-SFT-Server/tools/seed_rtu_user_role.sql）。销售演示模式可手动
      // localStorage.setItem('rtuDemoMode','1') 开启。
      if (result.data?.roleId === RTU_USER_ROLE_ID) {
        finalPath = '/clientMain/rtuDashboard'
      }
      localStorage.removeItem('rtuDemoMode')

      //ElMessage.error(`${finalPath}- ${result.data?.roleId}`);
      await router.push(finalPath)


    ElMessage.success({
      message: t('message.login.success'),
      duration: 1500
    })
     
  } catch (error) {
    console.error('登录失败:', error)
    ElMessage.error({
      message: error.message || t('message.login.failed'),
      duration: 3000
    })
  } finally {    
    loading.value = false
  }
}

// ===== 语言切换 =====
const loadLocale = async (lang) => {
  try {
    const module = await import(
      `element-plus/dist/locale/${lang === "zh-CN" ? "zh-cn" : "en"}.mjs`
    );
    elLocale.value = module.default;
  } catch (error) {
    console.error("Failed to load locale:", error);
    const fallback = await import("element-plus/dist/locale/en.mjs");
    elLocale.value = fallback.default;
  }
};

const changeLang = async (lang) => {
  try {
    locale.value = lang;
    localStorage.setItem("lang", lang);
    await loadLocale(lang);    
  } catch (error) {
    console.error('语言切换失败:', error);
  }
};
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: 
    linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  background-size: 300px 300px;
  background-repeat: repeat;
}

.login-box {
  width: 420px;
  padding: 30px 25px;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.login-title {
  text-align: center;
  margin-bottom: 30px;
  color: #409EFF;
  font-size: 24px;
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  padding: 0 15px;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #dcdfe6;
  &:hover {
    box-shadow: 0 0 0 1px #c0c4cc;
  }
  &.is-focus {
    box-shadow: 0 0 0 1px #409EFF !important;
  }
}

:deep(.el-input__inner) {
  height: 46px;
  line-height: 46px;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

.login-btn {
  width: 100%;
  margin-top: 10px;
  font-weight: 500;
}

:deep(.el-input__prefix .el-icon) {
  font-size: 20px;
}

@media (max-width: 480px) {
  .login-box {
    width: 90%;
    padding: 20px 15px;
  }
  .login-title {
    font-size: 20px;
  }
  :deep(.el-input__inner) {
    height: 42px;
    line-height: 42px;
  }
}
</style>